import { useEffect, useState } from "react";
import api from "../lib/api";
import {
  ResponsiveContainer, LineChart, Line, BarChart, Bar,
  XAxis, YAxis, Tooltip, CartesianGrid, Legend, PieChart, Pie, Cell,
} from "recharts";

const COLORS = ["#2563eb", "#16a34a", "#f97316", "#9333ea", "#dc2626"];
const TOOLTIP_STYLE = { borderRadius: 0, border: "1px solid #e2e8f0" };

export default function Reports() {
  const [stats, setStats] = useState(null);
  const [expenses, setExpenses] = useState([]);

  useEffect(() => {
    Promise.all([api.get("/dashboard/stats"), api.get("/expenses")])
      .then(([a, b]) => { setStats(a.data); setExpenses(b.data); });
  }, []);

  if (!stats) return <div className="text-slate-500">Loading…</div>;

  const byCat = expenses.reduce((acc, e) => {
    acc[e.category] = (acc[e.category] || 0) + e.amount;
    return acc;
  }, {});
  const pieData = Object.entries(byCat).map(([name, value]) => ({ name, value }));

  return (
    <div className="space-y-8" data-testid="reports-page">
      <div>
        <div className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold">Insights</div>
        <h1 className="font-display text-2xl sm:text-3xl md:text-4xl font-black tracking-tighter">Reports</h1>
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        <div className="bg-white border border-slate-200 p-6">
          <h2 className="font-display text-xl font-bold mb-6">Monthly income vs expenses</h2>
          <div className="h-72">
            <ResponsiveContainer>
              <LineChart data={stats.monthly || []}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="period" stroke="#64748b" fontSize={12} />
                <YAxis stroke="#64748b" fontSize={12} />
                <Tooltip contentStyle={TOOLTIP_STYLE} />
                <Legend />
                <Line type="monotone" dataKey="income" stroke="#16a34a" strokeWidth={2} />
                <Line type="monotone" dataKey="expense" stroke="#dc2626" strokeWidth={2} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="bg-white border border-slate-200 p-6">
          <h2 className="font-display text-xl font-bold mb-6">Expenses by category</h2>
          <div className="h-72">
            {pieData.length === 0 ? (
              <div className="h-full flex items-center justify-center text-slate-500">No expense data yet.</div>
            ) : (
              <ResponsiveContainer>
                <PieChart>
                  <Pie data={pieData} dataKey="value" nameKey="name" outerRadius={100} label>
                    {pieData.map((entry) => <Cell key={entry.name} fill={COLORS[pieData.indexOf(entry) % COLORS.length]} />)}
                  </Pie>
                  <Tooltip contentStyle={TOOLTIP_STYLE} />
                </PieChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>
      </div>

      <div className="bg-white border border-slate-200 p-6">
        <h2 className="font-display text-xl font-bold mb-6">Income breakdown</h2>
        <div className="h-72">
          <ResponsiveContainer>
            <BarChart data={stats.monthly || []}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="period" stroke="#64748b" fontSize={12} />
              <YAxis stroke="#64748b" fontSize={12} />
              <Tooltip contentStyle={TOOLTIP_STYLE} />
              <Bar dataKey="income" fill="#2563eb" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}
